import { Flex, Heading } from "@chakra-ui/react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import {
  dateRange6MonthsAgo,
  dateRange7DaysAgo,
  dateRange30DaysAgo,
  dateRange90DaysAgo,
  dateRangeCustom,
  SelectDateRangeState,
} from "../../../../components/SelectDateRange/SelectDateRange";
import {
  AiInterviewFeedbackSort,
  SortDirection,
  useAiInterviewFeedbackRatingSummaryQuery,
} from "../../../graphql";
import AnalyticsDateSelect from "../../../pages/analytics/AnalyticsDateSelect";
import { AiInterviewerCandidateFeedbackTable } from "./AiInterviewerCandidateFeedbackTable";
import {
  AiInterviewerFeedbackRatingCard,
  getFeedbackRatingBucketInterval,
} from "./AiInterviewerFeedbackRatingCard";
import { AiInterviewerInsightsEmptyState } from "./AiInterviewerInsightsEmptyState";
import { AiInterviewerRatingsCommentsCard } from "./AiInterviewerRatingsCommentsCard";

const DEFAULT_DATE_RANGE = "last_30";
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_PAGE = 1;
const DEFAULT_SORT = "date";
const DEFAULT_SORT_DIR = "desc";

const SORT_MAP: Record<string, AiInterviewFeedbackSort> = {
  date: AiInterviewFeedbackSort.Date,
  candidate: AiInterviewFeedbackSort.Candidate,
  rating: AiInterviewFeedbackSort.Rating,
  duration: AiInterviewFeedbackSort.Duration,
};

const SORT_REVERSE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(SORT_MAP).map(([k, v]) => [v, k])
);

const DATE_RANGE_FACTORIES = new Map<string, () => SelectDateRangeState>([
  ["last_6m", dateRange6MonthsAgo],
  ["last_90", dateRange90DaysAgo],
  ["last_30", dateRange30DaysAgo],
  ["last_7", dateRange7DaysAgo],
]);

type AiInterviewerInsightsProps = {
  aiInterviewerId: string;
};

export const AiInterviewerInsights: React.FC<AiInterviewerInsightsProps> = ({
  aiInterviewerId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialDateRange = useMemo(() => {
    const rangeType = searchParams.get("dateRange") || DEFAULT_DATE_RANGE;
    if (rangeType === "custom") {
      const start = searchParams.get("dateStart");
      const end = searchParams.get("dateEnd");
      if (start && end) {
        return dateRangeCustom(new Date(start), new Date(end));
      }
    }
    const factory = DATE_RANGE_FACTORIES.get(rangeType);
    return factory ? factory() : dateRange30DaysAgo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [dateRange, setDateRange] =
    useState<SelectDateRangeState>(initialDateRange);

  const initialPageSize = useMemo(() => {
    const v = searchParams.get("pageSize");
    return v ? Number(v) : DEFAULT_PAGE_SIZE;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialPage = useMemo(() => {
    const v = searchParams.get("page");
    return v ? Number(v) : DEFAULT_PAGE;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialSort = useMemo(() => {
    const v = searchParams.get("sort");
    return (v && SORT_MAP[v]) || AiInterviewFeedbackSort.Date;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialSortDir = useMemo(() => {
    const v = searchParams.get("sortDir");
    if (v === "asc") return SortDirection.Asc;
    if (v === "desc") return SortDirection.Desc;
    return SortDirection.Desc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [pageSize, setPageSize] = useState(initialPageSize);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [sortColumn, setSortColumn] =
    useState<AiInterviewFeedbackSort>(initialSort);
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(initialSortDir);
  const [minDate, setMinDate] = useState<Date | null>(null);
  const ratingBucketInterval = useMemo(
    () => getFeedbackRatingBucketInterval(dateRange.start, dateRange.end),
    [dateRange.end, dateRange.start]
  );

  const { data: currentSummaryData, loading: currentSummaryLoading } =
    useAiInterviewFeedbackRatingSummaryQuery({
      variables: {
        aiInterviewerId,
        dateStart: dateRange.start.toISOString(),
        dateEnd: dateRange.end.toISOString(),
        bucketInterval: ratingBucketInterval,
      },
    });

  const { data: lifetimeSummaryData, loading: lifetimeSummaryLoading } =
    useAiInterviewFeedbackRatingSummaryQuery({
      variables: {
        aiInterviewerId,
        bucketInterval: "DAY",
      },
    });

  const currentSummary = currentSummaryData?.aiInterviewFeedbackRatingSummary;
  const lifetimeSummary = lifetimeSummaryData?.aiInterviewFeedbackRatingSummary;
  const showEmptyState =
    !currentSummaryLoading && currentSummary?.reviewCount === 0;
  const noReviewsEver =
    !lifetimeSummaryLoading &&
    lifetimeSummary != null &&
    lifetimeSummary.reviewCount === 0;

  const isMountRef = useRef(true);
  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    setCurrentPage(DEFAULT_PAGE);
  }, [dateRange, pageSize, sortColumn, sortDirection]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    if (dateRange.type !== DEFAULT_DATE_RANGE) {
      params.set("dateRange", dateRange.type);
    } else {
      params.delete("dateRange");
    }
    if (dateRange.type === "custom") {
      params.set("dateStart", dateRange.start.toISOString().split("T")[0]);
      params.set("dateEnd", dateRange.end.toISOString().split("T")[0]);
    } else {
      params.delete("dateStart");
      params.delete("dateEnd");
    }

    if (pageSize !== DEFAULT_PAGE_SIZE) {
      params.set("pageSize", String(pageSize));
    } else {
      params.delete("pageSize");
    }

    if (currentPage !== DEFAULT_PAGE) {
      params.set("page", String(currentPage));
    } else {
      params.delete("page");
    }

    const sortKey = SORT_REVERSE_MAP[sortColumn] || DEFAULT_SORT;
    if (sortKey !== DEFAULT_SORT) {
      params.set("sort", sortKey);
    } else {
      params.delete("sort");
    }

    const dirKey = sortDirection === SortDirection.Asc ? "asc" : "desc";
    if (dirKey !== DEFAULT_SORT_DIR) {
      params.set("sortDir", dirKey);
    } else {
      params.delete("sortDir");
    }

    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, pageSize, currentPage, sortColumn, sortDirection]);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSortColumnChange = useCallback((col: AiInterviewFeedbackSort) => {
    setSortColumn(col);
  }, []);

  const handleSortDirectionChange = useCallback((dir: SortDirection) => {
    setSortDirection(dir);
  }, []);

  return (
    <Flex direction="column" gap={6} py={4}>
      <Flex justifyContent="space-between" alignItems="center" gap={4}>
        <Heading size="md" flex="1" fontWeight="600">
          Screen insights
        </Heading>
        <AnalyticsDateSelect
          disabled={showEmptyState && noReviewsEver}
          state={dateRange}
          onSelect={(newValue) => {
            if (newValue) setDateRange(newValue);
          }}
          minDate={minDate ?? undefined}
        />
      </Flex>
      {currentSummaryLoading ? null : showEmptyState && currentSummary ? (
        lifetimeSummaryLoading ? null : (
          <AiInterviewerInsightsEmptyState noReviewsEver={noReviewsEver} />
        )
      ) : (
        <>
          <AiInterviewerFeedbackRatingCard
            aiInterviewerId={aiInterviewerId}
            bucketInterval={ratingBucketInterval}
            dateStart={dateRange.start}
            dateEnd={dateRange.end}
            loadingSummary={currentSummaryLoading}
            summary={currentSummary}
          />
          <AiInterviewerRatingsCommentsCard
            aiInterviewerId={aiInterviewerId}
            bucketInterval={ratingBucketInterval}
            dateStart={dateRange.start}
            dateEnd={dateRange.end}
            loadingSummary={currentSummaryLoading}
            summary={currentSummary}
          />
          <AiInterviewerCandidateFeedbackTable
            aiInterviewerId={aiInterviewerId}
            dateStart={dateRange.start}
            dateEnd={dateRange.end}
            pageSize={pageSize}
            setPageSize={handlePageSizeChange}
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
            sortColumn={sortColumn}
            setSortColumn={handleSortColumnChange}
            sortDirection={sortDirection}
            setSortDirection={handleSortDirectionChange}
            onEarliestDateLoaded={setMinDate}
          />
        </>
      )}
    </Flex>
  );
};
